import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { DocumentService } from '@dealstack/frontend/shared/dataExchange/services/document/document.service';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { getRelatedToColumns } from '@dealstack/frontend/shared/components/Documents/DocumentDrawer/RelatedToModal/getRelatedToColumns';
import { useMantineTableWithPaginationFilteringSorting } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { EditDocument } from '@dealstack/frontend/shared/components/Documents/DocumentDrawer/types';
import { FindRelatedDocumentsRes } from '@dealstack/shared/lib/generated';
import { useMemo, useState } from 'react';
import { UseMantineTableDataOperationsPartialProps } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState';

interface Props {
	toggleModal: () => void;
	editDocument: EditDocument;
	isOpen: boolean;
	dealId: string;
	/**
	 * Called after links are saved, for hosts whose lists are keyed on different endpoints than the
	 * ones refetched below — the Document Library reads its own `document-library/*` queries.
	 */
	onLinksSaved?: () => Promise<void> | void;
}

export const useRelatedToModal = ({
	toggleModal,
	editDocument,
	isOpen,
	dealId,
	onLinksSaved,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const queryClient = useQueryClient();
	// Link toggles the user has made but not saved yet, keyed by transaction.
	const [linkOverrides, setLinkOverrides] = useState<Record<string, boolean>>({});

	// Absent in the Document Library, which lists documents deal-wide rather than under an
	// entity. The backend then derives the anchors from the document's own links instead.
	const transactionId = editDocument.target?.transactionId;

	const relatedPath = formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_DOCUMENTID_RELATED_GET, {
		dealId,
		documentId: editDocument.documentId,
	});

	const q = useQuery({
		queryKey: [
			relatedPath,
			{
				dealId,
				documentId: editDocument.documentId,
				transactionId,
			},
		],
		queryFn: ({ signal }) =>
			DocumentService.findRelatedTo(
				relatedPath,
				{
					dealId,
					documentId: editDocument.documentId,
					transactionId,
				},
				signal
			),
		enabled: isOpen,
		meta: { errorToast: tCapManage('documents.errors.get-document') },
	});

	/**
	 * DC-1118: derived from the query rather than copied into state. React Query's structural
	 * sharing hands back the *same* `data` reference when a refetch returns an identical payload,
	 * so an effect keyed on it never re-ran and the rows stayed empty once the modal had been
	 * closed and reopened.
	 */
	const relatedDocuments = useMemo<FindRelatedDocumentsRes[]>(
		() =>
			(q.data?.payload ?? []).map((related) => ({
				...related,
				isLinkedToEntity: linkOverrides[related.transactionId] ?? related.isLinkedToEntity,
			})),
		[q.data, linkOverrides]
	);

	const toggleRelatedRow = (transactionId: string) => {
		const row = relatedDocuments.find((related) => related.transactionId === transactionId);
		if (!row) {
			return;
		}
		setLinkOverrides((overrides) => ({
			...overrides,
			[transactionId]: !row.isLinkedToEntity,
		}));
	};

	const onClose = () => {
		setLinkOverrides({});
		toggleModal();
	};

	const updateRelatedToDocument = useMutation({
		mutationFn: DocumentService.updateTransactionRelatedDocuments,
	});

	const onSubmit = async () => {
		const linkPayload = relatedDocuments.map((relatedDocument) => {
			return {
				transactionId: relatedDocument.transactionId,
				isLinkedToEntity: relatedDocument.isLinkedToEntity,
				documentId: editDocument.documentId,
			};
		});

		updateRelatedToDocument.mutate(
			{
				updateData: linkPayload,
				dealId,
				documentId: editDocument.documentId,
			},
			{
				onSuccess: async () => {
					// DC-999: the next open renders these rows and reads the toggle direction
					// off them, so left stale "link" posts another unlink.
					await queryClient.refetchQueries({ queryKey: [relatedPath] });
					await queryClient.refetchQueries({
						queryKey: [
							formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_DOCUMENTID_GET, {
								dealId,
								documentId: editDocument.documentId,
							}),
						],
					});
					await queryClient.refetchQueries({
						queryKey: [
							formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_GET, {
								dealId,
							}),
						],
					});
					await onLinksSaved?.();
					onClose();
				},
				onError: () => {
					showToastV2({
						title: tCapManage('documents.errors.update-error'),
						state: 'error',
					});
				},
			}
		);
	};

	const columns = getRelatedToColumns({
		tCapManage,
		toggleRelatedRow,
	});

	const initialTableOperations: UseMantineTableDataOperationsPartialProps = {
		initialPagination: {
			pageIndex: 0,
			pageSize: 10,
		},
	};
	const { mantineTableDataOperations } = useMantineTableWithPaginationFilteringSorting(
		columns,
		initialTableOperations
	);

	return {
		relatedDocuments,
		columns,
		onClose,
		mantineTableDataOperations,
		onSubmit,
		isLoading: q.isPending,
		// A failed refetch keeps the last good payload in `q.data`, so without this the stale rows
		// stay on screen and stay actionable.
		isError: q.isError,
		isSubmitLoading: updateRelatedToDocument.isPending,
	};
};
